/**
 * Keeps the background videos (hero, banners) moving -- and never lets them
 * turn into a player.
 *
 * Two iOS cases a <video> cannot win on its own:
 *
 * - Low Power Mode: Safari refuses every autoplay, even muted, and paints a
 *   native play button over the frozen frame. play() rejects with
 *   NotAllowedError.
 * - In-app browsers that forbid inline playback (TikTok's): any play() that
 *   does get through opens the native fullscreen player instead of playing in
 *   the page.
 *
 * Both are solved the same way: swap the <video> for its animated-WebP twin
 * (data-motion-fallback). Animated images always play inline, loop forever,
 * and have no play button or fullscreen mode. Browsers that can autoplay keep
 * the real video.
 *
 * Never call play() from a tap or scroll here: in a no-inline browser that
 * gesture is exactly what opens the fullscreen player.
 */
(() => {
  const SELECTOR = 'video[data-hero-autoplay]';

  // In-app browsers known to push every video into the fullscreen player.
  const NO_INLINE = /musical_ly|BytedanceWebview|TikTok/i.test(navigator.userAgent);

  // Hidden videos (the desktop one on a phone, and vice versa) are left alone
  // so their fallback is only downloaded if that layout is ever shown.
  const isShown = (video) => video.getClientRects().length > 0;

  const toImage = (video) => {
    const src = video.dataset.motionFallback;
    if (!src || !video.isConnected || !isShown(video)) return false;

    const img = document.createElement('img');
    img.className = video.className;
    img.src = src;
    img.alt = '';
    img.decoding = 'async';
    img.setAttribute('aria-hidden', 'true');
    if (video.dataset.testid) img.dataset.testid = `${video.dataset.testid}-motion`;

    video.pause();
    video.replaceWith(img);
    // Stop the video download now that nothing shows it.
    video.querySelectorAll('source').forEach((source) => source.removeAttribute('src'));
    video.removeAttribute('src');
    video.load();
    return true;
  };

  // A video that reaches fullscreen anyway is sent straight back and replaced.
  const leaveFullscreen = (video) => {
    try {
      if (video.webkitDisplayingFullscreen) video.webkitExitFullscreen();
      if (document.fullscreenElement === video) document.exitFullscreen();
    } catch (error) {
      /* Nothing to exit. */
    }
    if (!toImage(video)) video.pause();
  };

  const start = (video) => {
    if (!video.isConnected || !video.paused) return;
    if (NO_INLINE || video.dataset.autoplayRefused === 'true') {
      toImage(video);
      return;
    }
    if (!isShown(video)) return;

    // Must be set as properties too; some WebViews only honour the attributes
    // if they are present before the element has a source attached.
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.loop = true;

    const attempt = video.play();
    if (attempt && typeof attempt.catch === 'function') {
      attempt.catch((error) => {
        // NotAllowedError is a refusal (Low Power Mode, no-gesture policy);
        // anything else -- e.g. AbortError while the source loads -- is
        // transient and the next media event retries.
        if (error && error.name === 'NotAllowedError') {
          video.dataset.autoplayRefused = 'true';
          toImage(video);
        }
      });
    }
  };

  const register = (video) => {
    if (video.dataset.heroAutoplayBound === 'true') return;
    video.dataset.heroAutoplayBound = 'true';

    video.addEventListener('webkitbeginfullscreen', () => leaveFullscreen(video));
    video.addEventListener('fullscreenchange', () => {
      if (document.fullscreenElement === video) leaveFullscreen(video);
    });

    if (NO_INLINE) {
      toImage(video);
      return;
    }

    // Each of these is a fresh chance to get playback going. Not 'pause':
    // closing a fullscreen player fires it, and replaying reopens the player.
    ['loadedmetadata', 'loadeddata', 'canplay', 'canplaythrough', 'stalled'].forEach((event) =>
      video.addEventListener(event, () => start(video))
    );

    start(video);
  };

  const scan = () => document.querySelectorAll(SELECTOR).forEach(register);

  // Coming back to the page (tab switch, back button) can leave a video paused.
  const resume = () => document.querySelectorAll(SELECTOR).forEach(start);

  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) resume();
  });
  window.addEventListener('pageshow', resume);
  // Crossing the mobile/desktop breakpoint reveals the other layout's video.
  window.addEventListener('resize', resume, { passive: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan, { once: true });
  } else {
    scan();
  }

  // The theme editor re-renders a section's markup in place, which throws away
  // the listeners bound above, so bind again on Shopify's own reload event.
  document.addEventListener('shopify:section:load', scan);
})();
